<template>
  <div class="aco-grid">
    <div class="aco-info-box">
      <b-card>
        <margin-fields v-if="organization" :fields="marginFields" />
        <delete-department-modal ref="confirmDepartmentDelete"/>
        <b-button type="submit" variant="danger" @click="deleteDepartment()" block>{{ $t('deleteDepartment') }}</b-button>
      </b-card>
    </div>

    <b-col v-if="department">
      <div class="aco-details-box">
        <b-col class="aco-details">
          <details-form :fields="departmentFields" :state-object="department"/>
          <b-row>
            <b-col md="12">
              <b-button type="button" class="float-left mt-3" @click="reloadOrganization">{{ $t('reset') }}</b-button>
              <b-button type="button" variant="primary" class="float-right mt-3" @click="saveDepartment">{{ $t('save') }}</b-button>
            </b-col>
          </b-row>
        </b-col>
      </div>

      <div class="aco-details-box">
        <b-col class="aco-details">
          <b-row>
            <b-col class="mb-2">
              <b-button type="button" variant="primary" class="float-right" @click="addLicenseModal">
                {{ $t('addLicense') }}
              </b-button>
              <h5 class="float-left">{{ $t('licenses') }}</h5>
            </b-col>
          </b-row>
          <template>
            <b-table show-empty striped hover small class="aco-table" :items="licenses" :fields="licenseFields">
              <template slot="empty">
                <div class="text-center">{{ $t('emptyTable')}}</div>
              </template>
              <template v-slot:cell(actions)="row">
                <b-button variant="danger" size="md" class="py-0 float-right" v-b-modal="`license-delete-${row.item.licenseId}`">{{ $t('delete') }}</b-button>
                <b-modal :id="`license-delete-${row.item.licenseId}`" no-stacking>
                  <template slot="modal-title">{{ $t('deleteLicense') }}</template>
                  <p class="my-0">{{ $t('deleteLicenseConfirm') }}</p>
                  <template slot="modal-footer">
                    <b-button-group>
                      <b-button variant="danger" v-on:click="deleteLicense(row.item)" :disabled="isLoading">
                        {{ $t('delete') }}
                      </b-button>
                      <b-button variant="secondary" @click="$bvModal.hide(`license-delete-${row.item.licenseId}`)" :disabled="isLoading">
                        {{ $t('cancel') }}
                      </b-button>
                    </b-button-group>
                  </template>
                </b-modal>
              </template>
            </b-table>
          </template>
        </b-col>
      </div>

      <add-department-license-modal ref="addLicenseDepartmentModal"/>

      <div class="aco-details-box">
        <b-col class="aco-details">
          <b-row>
            <b-col class="mb-2">
              <b-button type="button" variant="primary" class="float-right" @click="$refs['searchUser'].showModal()">
                {{ $t('addUser') }}
              </b-button>
              <user-search ref="searchUser" class="user-search" @selectedOwnerUser="addDepartmentUser" hidden />
              <span class="input-group-text"><b-icon-search @click="$refs['searchUser'].showModal()"/></span>
              <h5 class="float-left">{{ $t('users') }}</h5>
            </b-col>
          </b-row>
          <template>
            <b-table show-empty striped hover small class="aco-table" :items="sortedUsers" :fields="userFields">
              <template slot="empty">
                <div class="text-center">{{ $t('emptyTable')}}</div>
              </template>
              <template v-slot:cell(actions)="row">
                <b-button variant="danger" size="md" class="py-0 float-right" v-b-modal="`confirm-delete-${row.item.userId}`">{{ $t('delete') }}</b-button>
                <b-modal :id="`confirm-delete-${row.item.userId}`" no-stacking>
                  <template slot="modal-title">{{ $t('deleteUser') }}</template>
                  <p class="my-0">{{ $t('deleteUserConfirm') }}n</p>
                  <template slot="modal-footer">
                    <b-button-group>
                      <b-button variant="danger" v-on:click="deleteUser(row.item)" :disabled="isLoading">
                        {{ $t('delete') }}
                      </b-button>
                      <b-button variant="secondary" @click="$bvModal.hide(`confirm-delete-${row.item.userId}`)" :disabled="isLoading">
                        {{ $t('cancel') }}
                      </b-button>
                    </b-button-group>
                  </template>
                </b-modal>
              </template>
            </b-table>
          </template>
        </b-col>
      </div>
    </b-col>
  </div>
</template>

<script>
  import BaseComponent from '../../components/BaseComponent'
  import {mapGetters, mapMutations} from 'vuex'
  import MarginFields from '../../components/MarginFields'
  import DetailsForm from '../../components/DetailsForm'
  import DeleteDepartmentModal from './DeleteDepartmentModal'
  import UserSearch from "@/components/UserSearch.vue";
  import AddDepartmentLicenseModal from "@/views/OrganizationsPage/AddDepartmentLicenseModal.vue";

  export default {
    name: 'DepartmentDetails',
    extends: BaseComponent,
    components: {
      AddDepartmentLicenseModal,
      UserSearch,
      DeleteDepartmentModal,
      MarginFields,
      DetailsForm
    } ,

    data () {
      return {
        discounts: [],
        isLoading: false,
      }
    },
    created () {
      this.$store.dispatch('organizations/getOrganization', this.$route.params.id)
      this.$store.dispatch('organizations/getDepartment', this.$route.params.departmentId)
        .then(async () => {
          await this.$store.dispatch('licenses/resetFilter')
          this.$store.dispatch('organizations/getDepartmentUsers', this.$route.params.departmentId)
          this.setLicenseDepartment(this.$route.params.departmentId)
          this.$store.dispatch('licenses/getLicenses')
        })
    },
    computed: {
      ...mapGetters('organizations', ['department', 'organization']),
      ...mapGetters('licenses', ['licenses']),
      sortedUsers() {
        return (this.department?.users || []).toSorted((a, b) => a.fullName.localeCompare(b.fullName))
      },
      departmentFields() {
        return [
          { type: 'header', backButton: true, width: 12, displayName: this.$t('departmentInformation') },
          { name: 'name', label: this.$t('name'), type: 'text', readonly: false, width: 3,  displayName: this.$t('name')},
        ]
      },
      marginFields () {
        return [
          { name: 'organizationName', displayName: this.$t('organizationName'), value: this.organization.name  },
          { name: 'departmentId', displayName: this.$t('departmentId'), value: this.department?.departmentId },
          { name: 'departmentOrganizationId', displayName: this.$t('departmentOrganizationId'), value: this.department?.organizationNumber },
          { name: 'departmentSource', displayName: this.$t('departmentSource'), value: this.department?.organizationSource ? this.$t(`organizationSources.${this.department.organizationSource}`) : ''  },
        ]
      },
      userFields() {
        return [
          { key: 'fullName', label: this.$t('name'), type: 'text', readonly: false, width: 3,  displayName: this.$t('name')},
          { key: 'primaryRole', label: this.$t('primaryRole'), type: 'text', readonly: false, width: 3, displayName: this.$t('primaryRole'), formatter: value => value = this.$t(`userPrimaryRoles.${value.toUpperCase()}`) },
          { key: 'actions', label: '' }
        ]
      },
      licenseFields () {
        return [
          { key: 'licenseModel', label: this.$t('model'), tdClass: 'text-nowrap', formatter: value => this.translateAlternatives('licenseModels', value) },
          { key: 'channel', label: this.$t('channel'), sortable: false },
          { key: 'productName', label: this.$t('name'), sortable: true },
          { key: 'orderId', label: this.$t('orderId'), sortable: true, tdClass: 'text-nowrap' },
          { key: 'validFrom', label: this.$t('validFrom'), sortable: true, thClass: 'text-nowrap', tdClass: 'text-nowrap', formatter: 'formatDate' },
          { key: 'validTo', label: this.$t('validTo'), sortable: true, thClass: 'text-nowrap', tdClass: 'text-nowrap', formatter: 'formatDate' },
          { key: 'isBundle', label: this.$t('bundle'), formatter: 'translateBooleans' },
          { key: 'actions', label: '' }
        ]
      }
    },
    methods: {
      ...mapMutations('licenses', { setLicenseDepartment: 'UPDATE_DEPARTMENT' }),
      saveDepartment () {
        try {
          this.$store.dispatch('organizations/updateDepartment', this.department)
        } catch (error) {
          console.log(error)
          // @todo toast
        }
      },
      reloadOrganization () {
        this.$store.dispatch('organizations/getDepartment', this.$route.params.departmentId)
      },
      deleteDepartment () {
        this.$root.$emit('bv::show::modal', 'confirmDepartmentDelete')
      },
      addDepartmentUser (user) {
        this.$store.dispatch('organizations/addDepartmentUser', { department: this.department, user })
      },
      deleteUser(user) {
        this.$store.dispatch('organizations/deleteDepartmentUser', { department: this.department, user })
          .finally(() => this.$bvModal.hide(`confirm-delete-${user.userId}`))
      },
      addLicenseModal() {
        this.$root.$emit('bv::show::modal', 'addLicenseDepartmentModal')
      },
      async deleteLicense(license) {
        try {
          await this.$store.dispatch('organizations/deleteDepartmentLicense', {
            departmentId: this.$route.params.departmentId,
            licenseId: license.licenseId,
          })
        } catch (error) {
          console.error(error)
        } finally {
          setTimeout(async () => {
            await this.$store.dispatch('licenses/resetFilter')
            this.setLicenseDepartment(this.$route.params.departmentId)
            this.$store.dispatch('licenses/getLicenses')
          }, 300)
        }
      }
    },
    i18n: {
      messages: {
        en: {
          'deleteOrganization': 'Delete organization',
          'organizationInformation': 'Organization information',
          'numberOfPupils': 'Number of pupils',
          'numberOfPupilsNsr': 'Number of pupils (NSR)',
          'numberOfPupilsOverride': 'Number of pupils (override)',
          'nsr': 'NSR',
          'setActiveOrganization': 'Set active organization',
          'discount': 'Discount',
          'discountType': 'Discount type',
          'noDiscounts': 'No discounts',
          'gradesFrom': 'Grades from',
          'gradesTo': 'Grades to',
          'minimumLicenses': 'Minimum licenses',
          'uniqueDepartmentId': 'Department number must be unique',
          'departmentId': 'Department number',
          'departments': 'Departments',
          'departmentName': 'Department name',
          'departmentSource': 'Department source',
          'departmentsMissing': 'No departments',
          'departmentCreate': 'Create department',
          'deleteDepartment': 'Delete department',
          'departmentInformation': 'Department information',
          'departmentOrganizationId': 'Organization number',
          'addUser': 'Add user',
          'organizationName': 'Organization name',
          'model': 'Model',
          'addLicense': 'Add license',
          'deleteLicense': 'Delete license',
          'deleteLicenseConfirm': 'Confirm to delete the license',
          'deleteUser': 'Delete user',
          'deleteUserConfirm': 'Confirm to delete the user',
        },
        nb: {
          'deleteOrganization': 'Slett organisasjon',
          'organizationInformation': 'Organisasjonsinformasjon',
          'numberOfPupils': 'Antall elever',
          'numberOfPupilsNsr': 'Antall elever (NSR)',
          'numberOfPupilsOverride': 'Antall elever (overstyrt)',
          'nsr': 'NSR',
          'setActiveOrganization': 'Sett aktiv organisasjon',
          'discount': 'Rabatt',
          'discountType': 'Rabattype',
          'noDiscounts': 'Ingen rabatter',
          'gradesFrom': 'Årstrinn fra',
          'gradesTo': 'Årstrinn til',
          'minimumLicenses': 'Minimum',
          'uniqueDepartmentId': 'Avdelingsnummer må være unikt',
          'departmentId': 'Avdelingsnummer',
          'departments': 'Avdelinger',
          'departmentName': 'Avdelingsnavn',
          'departmentSource': 'Avdelingskilde',
          'departmentsMissing': 'Ingen avdelinger',
          'departmentCreate': 'Opprett avdeling',
          'deleteDepartment': 'Slett avdeling',
          'departmentInformation': 'Avdelingsinformasjon',
          'departmentOrganizationId': 'Organisasjonsnummer',
          'addUser': 'Legg til bruker',
          'organizationName': 'Organisasjonsnavn',
          'model': 'Modell',
          'addLicense': 'Legg til lisens',
          'deleteLicense': 'Slett lisens',
          'deleteLicenseConfirm': 'Bekreft sletting av lisensen',
          'deleteUser': 'Slett bruker',
          'deleteUserConfirm': 'Bekreft sletting av brukeren',
        }
      }
    }
  }
</script>
<style scoped>
.input-group-text {
  display: none
}
</style>